import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Project {
  id: string;
  name: string;
  thumbnailUrl?: string;
  updatedAt: Date;
  commentCount: number;
  status: "active" | "archived";
}

export const useProjects = () => {
  return useQuery({
    queryKey: ["projects"],
    queryFn: async (): Promise<Project[]> => {
      // Fetch projects
      const { data: projects, error } = await supabase
        .from("projects")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!projects || projects.length === 0) return [];

      // Enrich each project with computed data
      const enrichedProjects = await Promise.all(
        projects.map(async (project) => {
          // Get comment count
          const { data: commentCount } = await supabase.rpc(
            "get_project_comment_count",
            { project_id_param: project.id }
          );

          // Get last activity
          const { data: lastActivity } = await supabase.rpc(
            "get_project_last_activity",
            { project_id_param: project.id }
          );

          // Get thumbnail
          const { data: thumbnail } = await supabase.rpc(
            "get_project_thumbnail",
            { project_id_param: project.id }
          );

          return {
            id: project.id,
            name: project.name,
            thumbnailUrl: thumbnail ?? undefined,
            updatedAt: new Date(lastActivity ?? project.created_at),
            commentCount: commentCount ?? 0,
            status: project.status as "active" | "archived",
          };
        })
      );

      return enrichedProjects;
    },
  });
};
